import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  ScrollView,
} from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader } from '../components';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useRTLStyles } from '../hooks/useRTLStyles';

type BulkMode = 'vendors' | 'products';

interface BulkResult {
  created?: number;
  updated?: number;
  linked?: number;
  failed: number;
  errors: Array<{ row: number; error: string }>;
}

const ACCEPTED_TYPES = [
  'text/csv',
  'text/comma-separated-values',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

export const VendorBulkUploadScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const [mode, setMode] = useState<BulkMode>('vendors');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<BulkResult | null>(null);

  const handlePickAndUpload = async () => {
    try {
      const picked = await DocumentPicker.getDocumentAsync({ type: ACCEPTED_TYPES, copyToCacheDirectory: true });
      if (picked.canceled || !picked.assets?.[0]) return;
      const file = picked.assets[0];

      setUploading(true);
      setResult(null);
      const response = mode === 'vendors'
        ? await apiService.bulkUploadVendors(file.uri, file.name, file.mimeType || 'text/csv')
        : await apiService.bulkUploadVendorProducts(file.uri, file.name, file.mimeType || 'text/csv');

      if (response.statusCode === 1000 && response.data) {
        setResult(response.data);
        showNotification('success', 'Upload Complete', 'Bulk upload finished — see the summary below.');
      } else {
        showNotification('error', 'Upload Failed', response.errors?.[0]?.message || 'Failed to process file');
      }
    } catch (error: any) {
      showNotification('error', 'Upload Failed', error.message || 'Failed to process file');
    } finally {
      setUploading(false);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={<FormHeader title="Bulk Upload" onBack={() => navigateTo('vendors')} />}
    >
      <ScrollView style={styles.container} contentContainerStyle={styles.contentInner}>
        <View style={[styles.modeRow, { flexDirection: rtl.rowDirection }]}>
          <TouchableOpacity
            style={[styles.modeButton, mode === 'vendors' && styles.modeButtonActive]}
            onPress={() => { setMode('vendors'); setResult(null); }}
          >
            <Text style={[styles.modeButtonText, mode === 'vendors' && styles.modeButtonTextActive]}>Vendors</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.modeButton, mode === 'products' && styles.modeButtonActive]}
            onPress={() => { setMode('products'); setResult(null); }}
          >
            <Text style={[styles.modeButtonText, mode === 'products' && styles.modeButtonTextActive]}>Vendor-Product Links</Text>
          </TouchableOpacity>
        </View>

        {mode === 'vendors' ? (
          <View style={styles.infoCard}>
            <Text style={styles.infoTitle}>Expected columns</Text>
            <Text style={styles.infoText}>Name (required), Phone, Email, Address, CR Number, VAT Number, Notes</Text>
            <Text style={styles.infoHint}>
              A vendor matching an existing CR Number is updated; everything else is created new.
            </Text>
          </View>
        ) : (
          <View style={styles.infoCard}>
            <Text style={styles.infoTitle}>Expected columns</Text>
            <Text style={styles.infoText}>Vendor CR Number or Vendor Name (required), Product SKU or Product Name (required), Product Price</Text>
            <Text style={styles.infoHint}>
              An existing product is linked to the vendor. If it doesn't exist yet, it's created (Product Name + Product Price required) and pre-linked to the vendor.
            </Text>
          </View>
        )}

        <TouchableOpacity style={styles.uploadButton} onPress={handlePickAndUpload} disabled={uploading}>
          {uploading ? (
            <ActivityIndicator size="small" color={colors.white} />
          ) : (
            <>
              <Ionicons name="cloud-upload-outline" size={20} color={colors.white} />
              <Text style={styles.uploadButtonText}>Choose File &amp; Upload</Text>
            </>
          )}
        </TouchableOpacity>

        {result && (
          <View style={styles.resultCard}>
            <Text style={styles.resultTitle}>Results</Text>
            <View style={[styles.resultRow, { flexDirection: rtl.rowDirection }]}>
              {result.created !== undefined && (
                <View style={styles.resultStat}>
                  <Text style={styles.resultStatValue}>{result.created}</Text>
                  <Text style={styles.resultStatLabel}>Created</Text>
                </View>
              )}
              {result.updated !== undefined && (
                <View style={styles.resultStat}>
                  <Text style={styles.resultStatValue}>{result.updated}</Text>
                  <Text style={styles.resultStatLabel}>Updated</Text>
                </View>
              )}
              {result.linked !== undefined && (
                <View style={styles.resultStat}>
                  <Text style={styles.resultStatValue}>{result.linked}</Text>
                  <Text style={styles.resultStatLabel}>Linked</Text>
                </View>
              )}
              <View style={styles.resultStat}>
                <Text style={[styles.resultStatValue, result.failed > 0 && styles.resultStatValueError]}>{result.failed}</Text>
                <Text style={styles.resultStatLabel}>Failed</Text>
              </View>
            </View>
            {result.errors.length > 0 && (
              <>
                <Text style={styles.errorsTitle}>Errors (first {result.errors.length})</Text>
                {result.errors.map((e, idx) => (
                  <Text key={idx} style={styles.errorLine}>Row {e.row}: {e.error}</Text>
                ))}
              </>
            )}
          </View>
        )}
      </ScrollView>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background.base },
  contentInner: { padding: spacing.lg, paddingBottom: Platform.OS !== 'web' ? 120 : spacing.xl },
  modeRow: { gap: spacing.sm, marginBottom: spacing.lg },
  modeButton: {
    flex: 1,
    paddingVertical: spacing.sm,
    alignItems: 'center',
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  modeButtonActive: {
    borderColor: colors.primary,
    backgroundColor: (colors.primary as any) + '15',
  },
  modeButtonText: { fontSize: typography.fontSize.sm, color: colors.textDark, fontFamily: typography.fontFamily.medium },
  modeButtonTextActive: { color: colors.primary },
  infoCard: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    marginBottom: spacing.lg,
  },
  infoTitle: { fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.textDark, marginBottom: spacing.xs },
  infoText: { fontSize: typography.fontSize.sm, color: colors.textDark },
  infoHint: { fontSize: typography.fontSize.xs, color: colors.textLight, marginTop: spacing.sm },
  uploadButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    backgroundColor: colors.primary,
    borderRadius: designTokens.borderRadius.md,
    paddingVertical: spacing.md,
  },
  uploadButtonText: { color: colors.white, fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold },
  resultCard: {
    marginTop: spacing.lg,
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
  },
  resultTitle: { fontSize: typography.fontSize.base, fontWeight: '600', color: colors.textDark, marginBottom: spacing.sm },
  resultRow: { gap: spacing.lg, marginBottom: spacing.sm },
  resultStat: { alignItems: 'center' },
  resultStatValue: { fontSize: typography.fontSize.xl, fontWeight: '700', color: colors.primary },
  resultStatValueError: { color: colors.error },
  resultStatLabel: { fontSize: typography.fontSize.xs, color: colors.textLight },
  errorsTitle: { fontSize: typography.fontSize.sm, fontWeight: '600', color: colors.textDark, marginTop: spacing.sm, marginBottom: spacing.xs },
  errorLine: { fontSize: typography.fontSize.xs, color: colors.error, marginBottom: 2 },
});
